import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { createPortal } from "react-dom";
import { useQuery } from "@tanstack/react-query";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";
import { getSourceHistory } from "../api";
import SourceHistoryTimeline from "./SourceHistoryTimeline";

// Right-side drawer for the full audit log. Deliberately NOT a modal:
//   - No backdrop overlay → the user can still see (and click into) the form behind it.
//     Audit log is reference info, not interruption.
//   - position: fixed with createPortal so the drawer escapes the parent's scroll / clip
//     context and renders cleanly over the page.
//   - Escape key closes (keyboard parity with modals); explicit × in the header.
//
// Shares the React Query cache key with SourceLastModifiedLine so opening the drawer is
// instant when the inline summary already populated the cache.
const SourceHistoryDrawer = ({ open, onClose, sourceId, valueLabels }) => {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const { data, isLoading, error } = useQuery(
    ["source-history", sourceId],
    () => getSourceHistory(sourceId),
    { enabled: open && sourceId != null },
  );
  const entries = data?.entries || [];

  if (!open) return null;

  return createPortal(
    <aside
      aria-label="Change history"
      className="fixed inset-y-0 right-0 z-50 w-96 bg-card border-l border-card-bd flex flex-col"
    >
      {/* Generous header padding so the title isn't cramped against the top edge — finance
          users will spend time auditing here, not glancing-and-closing. */}
      <header className="flex items-center justify-between px-6 py-5 border-b border-card-bd">
        <div>
          <h3 className="text-sm font-medium text-text">Change history</h3>
          <p className="mt-0.5 text-[11px] text-text-3">
            {entries.length > 0
              ? `${entries.length} recorded ${entries.length === 1 ? "change" : "changes"}`
              : "Audit trail for this source"}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="close history"
          className="flex items-center justify-center w-7 h-7 rounded-ctrl text-text-3 hover:text-text-2 hover:bg-surface-2 transition-colors"
        >
          <FontAwesomeIcon icon={icon({ name: "xmark", style: "solid" })} className="text-sm" />
        </button>
      </header>
      <div className="flex-1 overflow-auto px-6 py-5">
        {isLoading && (
          <p className="text-xs text-text-2 italic">Loading history…</p>
        )}
        {error && (
          <div>
            <p className="text-xs text-danger">Couldn't load the change history.</p>
            {(error?.message || error?.status) && (
              <p className="mt-1 text-[11px] text-text-2 font-mono">
                {error.status ? `${error.status} ` : ""}{error.message || ""}
              </p>
            )}
          </div>
        )}
        {!isLoading && !error && <SourceHistoryTimeline entries={entries} valueLabels={valueLabels} />}
      </div>
    </aside>,
    document.body,
  );
};

SourceHistoryDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  sourceId: PropTypes.number,
  // Project-id → friendly-name map, threaded down to the timeline so the diff summaries
  // show "InstantViewAI" rather than the project UUID. Optional.
  valueLabels: PropTypes.object,
};

export default SourceHistoryDrawer;
